import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { formatCents } from "@/lib/money";
import { Plus, Target as TargetIcon, TrendingUp, Trash2, Pencil, X, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const PALETTE = ["#6750A4", "#0F5027", "#7D5260", "#B3261E", "#386A20", "#625B71"];

export default function MoneyGoals() {
  const [data, setData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [contribFor, setContribFor] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/money/goals");
    setData(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (g) => {
    if (!confirm(`Delete "${g.name}"?`)) return;
    await api.delete(`/money/goals/${g.id}`);
    toast.success("Deleted");
    load();
  };

  if (!data) return <div className="text-sm text-surface-onVariant">Loading goals…</div>;

  const totalSplit = data.goals.reduce((s, g) => s + (g.split_pct || 0), 0);
  const surplus = data.month_surplus_cents;

  return (
    <div data-testid="money-goals" className="space-y-6">
      {/* Surplus + split summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-hero bg-surface-container p-6 md:col-span-2 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-primary-container/60 blur-[70px] pointer-events-none" />
          <div className="relative">
            <div className="text-xs uppercase tracking-widest text-surface-onVariant inline-flex items-center gap-1.5">
              <TrendingUp className="w-3 h-3 text-primary" /> This month's surplus
            </div>
            <div className="text-4xl font-medium text-surface-on tabular-nums mt-2" data-testid="goals-surplus">{formatCents(surplus)}</div>
            <div className="text-xs text-surface-onVariant mt-1">
              {totalSplit}% allocated to goals · {100 - totalSplit}% unallocated
            </div>
            {/* Allocation bar */}
            {data.goals.length > 0 && (
              <div className="mt-4 h-3 bg-surface-containerHigh rounded-full overflow-hidden flex" data-testid="allocation-bar">
                {data.goals.map((g) => (
                  <div key={g.id} className="h-full transition-all duration-500 ease-md" style={{ width: `${g.split_pct || 0}%`, backgroundColor: g.color }} title={`${g.name} · ${g.split_pct}%`} />
                ))}
                {totalSplit < 100 && (
                  <div className="h-full bg-surface-containerHigh flex-1" />
                )}
              </div>
            )}
          </div>
        </div>
        <div className="rounded-hero bg-surface-container p-6">
          <div className="text-xs uppercase tracking-widest text-surface-onVariant">Total goals</div>
          <div className="text-3xl font-medium text-surface-on mt-2" data-testid="goals-count">{data.goals.length}</div>
          <button
            data-testid="goal-add"
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground text-sm font-medium px-4 py-2 active:scale-[0.98] hover:brightness-110 transition-[transform,filter] duration-200 ease-md"
          ><Plus className="w-4 h-4" /> New goal</button>
        </div>
      </div>

      {/* Goals list */}
      {data.goals.length === 0 && (
        <div className="rounded-hero bg-surface-container p-16 text-center">
          <TargetIcon className="w-10 h-10 mx-auto text-surface-onVariant" />
          <p className="text-sm text-surface-onVariant mt-3">No goals yet. Set one to see when you'll hit it.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="goals-list">
        {data.goals.map((g) => (
          <div key={g.id} data-testid={`goal-${g.id}`} className="rounded-hero bg-surface-container p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: g.color + "22", color: g.color }}>
                <TargetIcon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-lg font-medium text-surface-on truncate">{g.name}</div>
                  <div className="flex items-center gap-1">
                    <button data-testid={`goal-edit-${g.id}`} onClick={() => { setEditing(g); setDialogOpen(true); }} className="p-1.5 rounded-full text-surface-onVariant hover:bg-primary/8 hover:text-primary"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`goal-delete-${g.id}`} onClick={() => del(g)} className="p-1.5 rounded-full text-surface-onVariant hover:bg-danger-container hover:text-danger"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <div className="text-xs text-surface-onVariant mt-0.5">Target by {g.target_date}</div>

                {/* Progress */}
                <div className="mt-4 flex items-end justify-between gap-2">
                  <div>
                    <div className="text-2xl font-medium text-surface-on tabular-nums">{formatCents(g.current_cents)}</div>
                    <div className="text-xs text-surface-onVariant">of {formatCents(g.target_cents)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-medium text-surface-on tabular-nums">{g.progress_pct}%</div>
                    <div className="text-xs text-surface-onVariant">complete</div>
                  </div>
                </div>
                <div className="mt-3 h-2 bg-surface-containerHigh rounded-full overflow-hidden">
                  <div className="h-full transition-all duration-500 ease-md" style={{ width: `${Math.min(100, g.progress_pct)}%`, backgroundColor: g.color }} />
                </div>

                <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-surface-onVariant">Allocation</div>
                    <div className="text-surface-on font-medium mt-0.5">{g.split_pct}% of surplus · {formatCents(g.monthly_contribution_cents)}/mo</div>
                  </div>
                  <div>
                    <div className="text-surface-onVariant">Projected hit</div>
                    <div className="text-surface-on font-medium mt-0.5" data-testid={`goal-projected-${g.id}`}>
                      {g.projected_hit_month || "—"}
                      {g.projected_hit_month && g.target_date && (
                        <span className={cn("ml-1 text-[10px]",
                          g.projected_hit_month <= g.target_date.slice(0, 7) ? "text-primary" : "text-danger"
                        )}>
                          {g.projected_hit_month <= g.target_date.slice(0, 7) ? "on track" : "behind"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  data-testid={`goal-contribute-${g.id}`}
                  onClick={() => setContribFor(g)}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary-container text-primary-onContainer text-sm font-medium px-4 py-2 hover:brightness-95 active:scale-[0.98] transition-[transform,filter] duration-200 ease-md"
                ><Plus className="w-3.5 h-3.5" /> Contribute</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <GoalDialog open={dialogOpen} onOpenChange={setDialogOpen} goal={editing} onSaved={load} />
      <ContributeDialog goal={contribFor} onOpenChange={(o) => !o && setContribFor(null)} onSaved={load} />
      <p className="text-[10px] text-surface-onVariant text-center pt-2">
        Estimates only — projected hit-months assume constant surplus and allocation. Not financial advice.
      </p>
    </div>
  );
}

function GoalDialog({ open, onOpenChange, goal, onSaved }) {
  const isEdit = !!goal;
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [current, setCurrent] = useState("");
  const [targetDate, setTargetDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [color, setColor] = useState(PALETTE[0]);
  const [splitPct, setSplitPct] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (goal) {
      setName(goal.name); setTarget((goal.target_cents / 100).toFixed(0));
      setCurrent((goal.current_cents / 100).toFixed(0));
      setTargetDate(goal.target_date); setColor(goal.color); setSplitPct(goal.split_pct);
    } else {
      setName(""); setTarget(""); setCurrent("0");
      setTargetDate(new Date(Date.now() + 180 * 86400000).toISOString().slice(0, 10));
      setColor(PALETTE[0]); setSplitPct(0);
    }
  }, [open, goal]);

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name required");
    const t = Math.round(parseFloat(target || "0") * 100);
    if (t <= 0) return toast.error("Target must be > 0");
    setSaving(true);
    try {
      const payload = {
        name: name.trim(), target_cents: t, target_date: targetDate,
        current_cents: Math.max(0, Math.round(parseFloat(current || "0") * 100)),
        color, split_pct: Math.max(0, Math.min(100, parseInt(splitPct) || 0)),
      };
      if (isEdit) await api.patch(`/money/goals/${goal.id}`, payload);
      else await api.post("/money/goals", payload);
      toast.success(isEdit ? "Goal updated" : "Goal created");
      onOpenChange(false); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-surface rounded-hero border-none p-6" data-testid="goal-dialog">
        <DialogHeader><DialogTitle className="text-surface-on text-lg font-medium">{isEdit ? "Edit goal" : "New goal"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4 mt-2">
          <Field label="Name"><input data-testid="goal-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Emergency fund" className="bg-transparent outline-none w-full text-surface-on" required /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Target" prefix="$"><input data-testid="goal-target" type="number" step="1" value={target} onChange={(e) => setTarget(e.target.value)} className="bg-transparent outline-none w-full text-surface-on" required /></Field>
            <Field label="Currently saved" prefix="$"><input data-testid="goal-current" type="number" step="1" value={current} onChange={(e) => setCurrent(e.target.value)} className="bg-transparent outline-none w-full text-surface-on" /></Field>
          </div>
          <Field label="Target date"><input data-testid="goal-date" type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} className="bg-transparent outline-none w-full text-surface-on" required /></Field>
          <div>
            <div className="text-xs font-medium tracking-widest uppercase text-surface-onVariant mb-1">Allocation of monthly surplus: {splitPct}%</div>
            <input data-testid="goal-split" type="range" min="0" max="100" value={splitPct} onChange={(e) => setSplitPct(e.target.value)} className="w-full accent-primary" />
          </div>
          <div>
            <div className="text-xs font-medium tracking-widest uppercase text-surface-onVariant mb-1">Colour</div>
            <div className="flex gap-2">
              {PALETTE.map((c) => (
                <button key={c} type="button" onClick={() => setColor(c)} className={cn("w-8 h-8 rounded-full border-2 transition-transform duration-200 ease-md", color === c ? "border-surface-on scale-110" : "border-transparent")} style={{ backgroundColor: c }} aria-label={`Color ${c}`} />
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="px-5 py-2.5 rounded-full text-sm font-medium text-surface-on hover:bg-primary/8">Cancel</button>
            <button data-testid="goal-save" type="submit" disabled={saving} className="px-5 py-2.5 rounded-full text-sm font-medium bg-primary text-primary-foreground active:scale-[0.98] hover:brightness-110 disabled:opacity-60 transition-[transform,filter] duration-200 ease-md">
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create goal"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ContributeDialog({ goal, onOpenChange, onSaved }) {
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (goal) setAmount(""); }, [goal]);

  const save = async (e) => {
    e.preventDefault();
    const cents = Math.round(parseFloat(amount || "0") * 100);
    if (cents <= 0) return toast.error("Enter an amount");
    setSaving(true);
    try {
      await api.post(`/money/goals/${goal.id}/contribute`, { amount_cents: cents });
      toast.success(`Added ${(cents / 100).toFixed(0)} to ${goal.name}`);
      onOpenChange(false); onSaved();
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!goal} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm bg-surface rounded-hero border-none p-6" data-testid="contribute-dialog">
        <DialogHeader><DialogTitle className="text-surface-on text-lg font-medium">Contribute to {goal?.name}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="mt-3 space-y-4">
          <Field label="Amount" prefix="$"><input data-testid="contribute-amount" type="number" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus className="bg-transparent outline-none w-full text-surface-on" required /></Field>
          <button data-testid="contribute-save" type="submit" disabled={saving} className="w-full rounded-full bg-primary text-primary-foreground font-medium py-3 active:scale-[0.98] hover:brightness-110 disabled:opacity-60 transition-[transform,filter] duration-200 ease-md">
            {saving ? "Saving…" : "Add contribution"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, prefix, children }) {
  return (
    <div>
      <div className="text-xs font-medium tracking-widest uppercase text-surface-onVariant mb-1">{label}</div>
      <div className="flex items-center bg-surface-containerHigh rounded-t-lg border-b-2 border-outline focus-within:border-primary px-3 py-2.5 transition-colors duration-200 ease-md">
        {prefix && <span className="text-surface-onVariant mr-1">{prefix}</span>}
        {children}
      </div>
    </div>
  );
}
